import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import { StyledCellColumn } from '@/components/custom-ui/TableColumns/StyledCell';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

import ActionsCol from './ActionsCol';
import ClientItemNameCol from './ClientItemNameCol';
import DiscountEditingCell from './DiscountCol';
import PriceCol from './PriceCol';
import QtyEditingCell from './QtyEditCol';
import RetailDiscountStatusCol from './RetailDiscountStatusCol';
import UnderRequestCol from './UnderRequestCol';
import WholeSaleDiscountStatusCol from './WholeSaleDiscountStatusCol copy';

// Pre-define static styles
const CELL_STYLES = {
  base: 'h-16 flex items-center justify-center',
  retail:
    'h-16 flex items-center justify-center rounded-md border border-indigo-200/50 bg-indigo-100/80 px-1.5 font-roboto text-lg font-bold text-indigo-700 dark:border-indigo-800/30 dark:bg-indigo-900/30 dark:text-indigo-100',
  wholesale:
    'h-16 flex items-center justify-center rounded-md border border-amber-200/50 bg-amber-100/80 px-1.5 font-roboto text-lg font-bold text-amber-700 dark:border-amber-800/30 dark:bg-amber-900/30 dark:text-amber-200',
  statusCell: 'h-16 flex items-center justify-center text-xs',
};

type Props = {
  handleShowCompetitors: (value: boolean) => void;
  handleShowItemGroup: (value: boolean) => void;
  handleShowUnderRequest: (value: boolean) => void;
  handleShowPurchaseHistory: (value: boolean) => void;
  handleSelectItem: (item: IOneItemInPrepareForRequest) => void;
  handleOpenEditDialog: (item: IOneItemInPrepareForRequest) => void;
  handleUpdateOneItemLink: (item: IOneItemInPrepareForRequest) => void;
  handleLoadingStage: (
    value: boolean,
    type: 'check' | 'upload' | 'update' | 'main',
  ) => void;
};

function usePrepareForRequestColumns({
  handleSelectItem,
  handleShowUnderRequest,
  handleShowCompetitors,
  handleShowItemGroup,
  handleShowPurchaseHistory,
  handleOpenEditDialog,
  handleUpdateOneItemLink,
  handleLoadingStage,
}: Props) {
  return useMemo<ColumnDef<IOneItemInPrepareForRequest>[]>(
    () => [
      {
        accessorKey: 'totalUnderRequests',
        header: 'طلب',
        enableEditing: false,
        enableGrouping: false,
        size: 50,
        cell: ({ row }) => {
          return (
            <UnderRequestCol
              handleSelectItem={handleSelectItem}
              handleShowUnderRequest={handleShowUnderRequest}
              item={row?.original}
            />
          );
        },
      },
      {
        accessorKey: 'ClientItemName',
        header: ' اسم الصنف',
        filterFn: 'includesString',
        enableGrouping: false,
        size: 300,
        cell: ({ row }) => {
          return (
            <ClientItemNameCol
              handleOpenEditDialog={handleOpenEditDialog}
              item={row?.original}
            />
          );
        },
      },
      {
        accessorKey: 'Price',
        header: 'السعر',
        size: 70,
        filterFn: 'CustomFilter',
        enableEditing: false,
        enableColumnFilter: true,
        enableGrouping: false,
        cell: ({ row }) => <PriceCol item={row?.original} />,
      },
      {
        accessorKey: 'DiscountStatus',
        header: 'حالة الخصم',
        enableEditing: false,
        size: 100,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;
          const status = row.getValue(columnId) as string;
          return status === filterValue;
        },
        enableGrouping: false,
        cell: ({ row }) => {
          const status = row.getValue('DiscountStatus') as string;
          return (
            <div className={CELL_STYLES.statusCell}>{status || 'N/A'}</div>
          );
        },
      },

      {
        accessorKey: 'Qty',
        header: 'الكمية',
        size: 120,
        enableColumnFilter: false,
        enableGrouping: false,
        cell: (cellContext) => {
          return <QtyEditingCell {...cellContext} />;
        },
      },
      {
        accessorKey: 'DiscountEdit',
        header: 'خصم',
        enableColumnFilter: false,
        enableGrouping: false,
        size: 100,
        cell: (cellContext) => {
          return (
            <DiscountEditingCell
              {...cellContext}
              handleSelectItem={handleSelectItem}
              handleShowCompetitors={handleShowCompetitors}
            />
          );
        },
      },

      {
        accessorKey: 'TotalAfterDiscount',
        header: 'اجمالي',
        enableColumnFilter: false,
        enableGrouping: false,
        enableEditing: false,
        size: 100,
        cell: ({ row }) => {
          return (
            <StyledCellColumn
              className="h-16 w-full border border-indigo-300/50 dark:border-indigo-800/30"
              value={row?.original?.TotalAfterDiscount || 0}
              color="indigo"
            />
          );
        },
      },
      {
        accessorKey: 'HealthyDiscountIN',
        header: 'المرجح',
        enableColumnFilter: false,
        enableGrouping: false,
        enableEditing: false,
        size: 100,
        cell: ({ row }) => {
          return (
            <div className="w-full">
              <StyledCellColumn
                color="yellow"
                className="h-16 border border-amber-300/50 px-2 dark:border-amber-800/30"
                value={
                  formatNumberWithCommas(row?.original?.HealthyDiscountIN, 3) ||
                  '-'
                }
              />
            </div>
          );
        },
      },

      {
        accessorKey: 'RetailStock',
        header: 'رصيد ق',
        enableediting: false,
        enableGrouping: false,
        size: 70,
        cell: ({ row }) => (
          <div className={CELL_STYLES.retail}>
            <span>{formatNumberWithCommas(row?.original?.RetailStock, 2)}</span>
          </div>
        ),
      },
      {
        accessorKey: 'RetailSales',
        header: 'بيع ق',
        enableediting: false,
        enableGrouping: false,
        size: 70,
        cell: ({ row }) => (
          <div className={CELL_STYLES.retail}>
            <span>{formatNumberWithCommas(row?.original?.RetailSales, 2)}</span>
          </div>
        ),
      },
      {
        accessorKey: 'RetailDiff',
        header: 'حالة ق',
        size: 70,
        enableEditing: false,
        cell: ({ row }) => {
          return <RetailDiscountStatusCol item={row?.original} />;
        },
      },
      {
        accessorKey: 'RetailStatus',
        header: 'تقرير ق',
        enableEditing: false,
      },
      {
        accessorKey: 'isLastUpdate',
        header: 'تحديث',
        enableEditing: false,
        filterFn: 'BooleanFilter',
      },

      {
        accessorKey: 'WholeSaleStock',
        header: 'رصيد ج',
        enableediting: false,
        enableGrouping: false,
        size: 70,
        cell: ({ row }) => (
          <div className={CELL_STYLES.wholesale}>
            <span>
              {formatNumberWithCommas(row?.original?.WholeSaleStock, 2)}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'WholeSaleSalesStatus',
        header: 'تقرير ق',
        enableEditing: false,
      },
      {
        accessorKey: 'WholeSaleSales',
        header: 'بيع ج',
        size: 70,
        enableediting: false,
        enableGrouping: false,
        cell: ({ row }) => (
          <div className={CELL_STYLES.wholesale}>
            <span>
              {formatNumberWithCommas(row?.original?.WholeSaleSales, 2)}
            </span>
          </div>
        ),
      },

      {
        accessorKey: 'WholeSaleDiff',
        header: 'حالة ج',
        size: 70,
        enableEditing: false,
        enableGrouping: false,
        cell: ({ row }) => {
          return <WholeSaleDiscountStatusCol item={row?.original} />;
        },
      },
      {
        accessorKey: 'minDiscount',
        header: '',
        size: 120,
        enableEditing: false,
        enableGrouping: false,
        enableColumnFilter: false,
        cell: (cell) => {
          return (
            <ActionsCol
              handleSelectItem={handleSelectItem}
              handleShowItemGroup={handleShowItemGroup}
              handleOpenEditDialog={handleOpenEditDialog}
              handleUpdateOneItemLink={handleUpdateOneItemLink}
              handleShowPurchaseHistory={handleShowPurchaseHistory}
              CellContext={cell}
              handleLoadingStage={handleLoadingStage}
            />
          );
        },
      },
    ],

    [
      handleLoadingStage,
      handleOpenEditDialog,
      handleSelectItem,
      handleShowCompetitors,
      handleShowItemGroup,
      handleShowUnderRequest,
      handleShowPurchaseHistory,
      handleUpdateOneItemLink,
    ],
  );
}

export default usePrepareForRequestColumns;
